import { useMutation, useQueryClient } from 'react-query'
import { UseMutationOptions } from 'react-query/types/react/types'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { BrandPlanScheduleContent } from 'types'
import { scheduleKeys } from './scheduleKeys'

interface Props {
  scheduleId: number
  contentId: number
}

export type Variables = Partial<Omit<BrandPlanScheduleContent, 'id' | 'brand_plan_schedule_id' | 'created_at'>>

interface Data {
  content: BrandPlanScheduleContent
}

interface Context {
  previousContent?: BrandPlanScheduleContent
}

export type Options = Omit<UseMutationOptions<Data, Error, Variables, Context>, 'mutationKey' | 'mutationFn'>

export const updateBrandPlanScheduleContent = async (
  scheduleId: number,
  contentId: number,
  variables: Variables,
): Promise<Data> => {
  const response = await sendToApi<Data>(`/schedules/${scheduleId}/contents/${contentId}`, {
    method: 'PUT',
    body: { ...variables },
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(response.data)
  }

  return response.data
}

export const useUpdateBrandPlanScheduleContent = ({ scheduleId, contentId }: Props, options?: Options) => {
  const queryClient = useQueryClient()

  return useMutation<Data, Error, Variables, Context>(
    scheduleKeys.contentDetail(scheduleId, contentId),
    (variables) => updateBrandPlanScheduleContent(scheduleId, contentId, variables),
    {
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: scheduleKeys.contentDetail(scheduleId, contentId) })

        const previousData = queryClient.getQueryData<Data>(scheduleKeys.contentDetail(scheduleId, contentId))
        const previousContent = previousData?.content

        if (!previousContent) {
          return undefined
        }

        queryClient.setQueryData<Data>(scheduleKeys.contentDetail(scheduleId, contentId), {
          content: { ...previousContent, ...variables },
        })

        return { previousContent }
      },
      onError: (_err, _variables, context) => {
        if (context?.previousContent) {
          queryClient.setQueryData<Data>(scheduleKeys.contentDetail(scheduleId, contentId), {
            content: context.previousContent,
          })
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries(scheduleKeys.contentDetail(scheduleId, contentId))
        queryClient.invalidateQueries(scheduleKeys.contents(scheduleId))
      },
      ...options,
    },
  )
}
